import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageFlag from '../common/LanguageFlag';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ScrollableDialog from '../common/ScrollableDialog';
import SwitchField from '../common/SwitchField';
import VatClassificationFields from '../common/VatClassificationFields';
import { fieldLabelSx, nativeInputSx } from '../common/formFieldStyles';
import { SUPPORTED_LANGUAGE_CODES } from '../../constants/Languages';
import ImagePickerField from '../common/ImagePickerField';
import ProductContentFields from './ProductContentFields';
import ProductStockSummary from './ProductStockSummary';
import { readVatClassification } from '../../utils/vatClassification';
import { buildProductSavePayload, validateProductForm } from '../../utils/productValidation';
import { resolvedContentQuantity, toPackageQuantity } from '../../utils/stockQuantityUtils';
import { BRAND } from '../../theme/brandTokens';

const buildLocalizedFields = (supportedLanguages, values = {}) => (
  Object.fromEntries(supportedLanguages.map((language) => [language, values?.[language] || '']))
);

const buildInitialProduct = (supportedLanguages, product) => {
  const contentQuantity = product?.contentQuantity ?? 1;
  const lowStockPackages = product?.lowStockThreshold == null || product?.lowStockThreshold === ''
    ? ''
    : String(toPackageQuantity(product.lowStockThreshold, contentQuantity));

  return {
    id: product?.id ?? null,
    name: buildLocalizedFields(supportedLanguages, product?.name),
    description: buildLocalizedFields(supportedLanguages, product?.description),
    price: product?.price ?? '',
    isRetail: product?.isRetail !== false,
    stockUnit: product?.stockUnit || 'Unit',
    contentQuantity: resolvedContentQuantity(product?.stockUnit || 'Unit', product?.contentQuantity ?? 1),
    lowStockPackages,
    stockOnHand: product?.stockOnHand ?? 0,
    lastUnitCost: product?.lastUnitCost ?? null,
    lastPurchaseDate: product?.lastPurchaseDate ?? null,
    hasStockMovements: Boolean(product?.hasStockMovements),
    disabled: Boolean(product?.disabled),
    imageUrl: product?.imageUrl || '',
    imageFile: null,
    removeImage: false,
    ...readVatClassification(product),
  };
};

const ProductEditorDialog = ({
  open,
  mode = 'create',
  product,
  supportedLanguages = ['en'],
  currency,
  canSeeCosts = false,
  saving = false,
  onClose,
  onSave,
  onImmediateImageSelect,
  onImmediateImageRemove,
}) => {
  const { t } = useTranslation();
  const languages = useMemo(
    () => (supportedLanguages?.length ? supportedLanguages : ['en']),
    [supportedLanguages],
  );
  const sortedLanguages = useMemo(
    () => [...languages].sort((left, right) => (
      SUPPORTED_LANGUAGE_CODES.indexOf(left) - SUPPORTED_LANGUAGE_CODES.indexOf(right)
    )),
    [languages],
  );
  const [activeLanguage, setActiveLanguage] = useState(sortedLanguages[0] || 'en');
  const [formData, setFormData] = useState(() => buildInitialProduct(languages, product));
  const [errors, setErrors] = useState({});
  const [previewUrl, setPreviewUrl] = useState(product?.imageUrl || '');

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextForm = buildInitialProduct(languages, product);
    setFormData(nextForm);
    setErrors({});
    setPreviewUrl(product?.imageUrl || '');
    setActiveLanguage(sortedLanguages[0] || 'en');
  }, [open, product, languages, sortedLanguages]);

  useEffect(() => {
    if (!formData.imageFile) {
      return undefined;
    }

    const objectUrl = URL.createObjectURL(formData.imageFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.imageFile]);

  const handleSubmit = () => {
    const nextErrors = validateProductForm(formData, languages);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSave(buildProductSavePayload(formData, languages));
  };

  const handleSelectImage = (file) => {
    if (mode === 'edit' && onImmediateImageSelect) {
      onImmediateImageSelect(file);
      return;
    }

    setFormData((previous) => ({
      ...previous,
      imageFile: file,
      removeImage: false,
    }));
  };

  const handleRemoveImage = () => {
    if (mode === 'edit' && onImmediateImageRemove) {
      onImmediateImageRemove();
      setPreviewUrl('');
      return;
    }

    setFormData((previous) => ({
      ...previous,
      imageFile: null,
      removeImage: true,
    }));
    setPreviewUrl('');
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      title={mode === 'edit' ? t('productsManagement.dialog.editTitle') : t('productsManagement.dialog.createTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} disabled={saving} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton id="product-editor-save" onClick={handleSubmit} disabled={saving}>
            {saving
              ? t('common.saving')
              : mode === 'edit'
                ? t('productsManagement.dialog.saveEdit')
                : t('productsManagement.dialog.saveCreate')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box id="product-editor-form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: errors.name ? 'error.main' : 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeLanguage}
            onChange={(_, nextLanguage) => setActiveLanguage(nextLanguage)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              backgroundColor: BRAND.background,
              '& .MuiTab-root': {
                color: BRAND.textMuted,
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48,
              },
              '& .Mui-selected': {
                color: BRAND.accentText,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: BRAND.accent,
              },
            }}
          >
            {sortedLanguages.map((language) => (
              <Tab
                key={language}
                value={language}
                label={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <LanguageFlag languageCode={language} size="1rem" />
                    <Typography variant="body2">{t(`common.languages.${language}`)}</Typography>
                  </Box>
                )}
              />
            ))}
          </Tabs>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: 2.5 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {`${t('productsManagement.fields.name')} (${t(`common.languages.${activeLanguage}`)}) *`}
              </Typography>
              <Box
                component="input"
                id={`product-name-${activeLanguage}`}
                type="text"
                aria-label={`${t('productsManagement.fields.name')} (${t(`common.languages.${activeLanguage}`)})`}
                value={formData.name?.[activeLanguage] || ''}
                onChange={(event) => setFormData((previous) => ({
                  ...previous,
                  name: {
                    ...previous.name,
                    [activeLanguage]: event.target.value,
                  },
                }))}
                sx={nativeInputSx(Boolean(errors.name))}
              />
              {errors.name ? (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                  {t(errors.name)}
                </Typography>
              ) : null}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {`${t('productsManagement.fields.description')} (${t(`common.languages.${activeLanguage}`)})`}
              </Typography>
              <Box
                component="textarea"
                id={`product-description-${activeLanguage}`}
                rows={4}
                aria-label={`${t('productsManagement.fields.description')} (${t(`common.languages.${activeLanguage}`)})`}
                value={formData.description?.[activeLanguage] || ''}
                onChange={(event) => setFormData((previous) => ({
                  ...previous,
                  description: {
                    ...previous.description,
                    [activeLanguage]: event.target.value,
                  },
                }))}
                sx={nativeInputSx(false, true)}
              />
            </Box>
          </Box>
        </Box>

        <SwitchField
          id="product-is-retail"
          checked={formData.isRetail !== false}
          onChange={(event) => setFormData((previous) => ({ ...previous, isRetail: event.target.checked }))}
          label={t('productsManagement.fields.soldToCustomers')}
          helpText={t('productsManagement.fields.soldToCustomersHelp')}
        />

        {formData.isRetail !== false ? (
          <Box>
            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {t('productsManagement.fields.price')} *
            </Typography>
            <Box
              component="input"
              id="product-price"
              type="number"
              min={0}
              step="0.01"
              aria-label={t('productsManagement.fields.price')}
              value={formData.price}
              onChange={(event) => setFormData((previous) => ({ ...previous, price: event.target.value }))}
              sx={nativeInputSx(Boolean(errors.price))}
            />
            {errors.price ? (
              <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                {t(errors.price)}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <ProductContentFields
          contentQuantity={formData.contentQuantity}
          stockUnit={formData.stockUnit}
          lowStockPackages={formData.lowStockPackages}
          errors={errors}
          onChange={(patch) => setFormData((previous) => ({ ...previous, ...patch }))}
        />

        <ProductStockSummary
          stockOnHand={formData.stockOnHand}
          contentQuantity={formData.contentQuantity}
          stockUnit={formData.stockUnit}
          lastUnitCost={formData.lastUnitCost}
          lastPurchaseDate={formData.lastPurchaseDate}
          canSeeCosts={canSeeCosts}
          currency={currency}
        />

        <VatClassificationFields
          idPrefix="product-vat"
          value={formData}
          errors={errors}
          onChange={(next) => setFormData((previous) => ({ ...previous, ...next }))}
        />

        <SwitchField
          id="product-is-active"
          checked={!formData.disabled}
          onChange={(event) => setFormData((previous) => ({ ...previous, disabled: !event.target.checked }))}
          label={t('productsManagement.fields.isActive')}
          helpText={t('productsManagement.fields.isActiveHelp')}
        />

        <ImagePickerField
          label={t('productsManagement.fields.image')}
          helpText={t('productsManagement.fields.imageHelp')}
          imageUrl={previewUrl}
          onSelect={handleSelectImage}
          onRemove={handleRemoveImage}
          disabled={saving}
          removeConfirmMessage={t('productsManagement.fields.imageRemoveConfirm')}
        />
      </Box>
    </ScrollableDialog>
  );
};

export default ProductEditorDialog;
